import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Compressores from '~/pages/Compressores/Compressores';
import {
  Description,
  ErrorMessage,
  Eyebrow,
  Field,
  Form,
  FullWidthField,
  GuestPage,
  Input,
  Select,
  SubmitButton,
  Title,
} from './GuestCompressorAccess_Styles';

type ProductType = 'hermetico' | 'semi-hermetico';
type Params = { productType: ProductType };

const SESSION_KEY = 'coldstorage_guest_compressor_session';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const maskCnpj = (value: string) => {
  const digits = onlyDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const maskWhatsapp = (value: string) => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  const separator = digits.length >= 11 ? '-' : '';
  return `(${digits.slice(0, 2)}) ${digits.slice(2, digits.length >= 11 ? 7 : 6)}${separator}${digits.slice(digits.length >= 11 ? 7 : 6)}`;
};

const isValidCnpj = (value: string) => {
  const digits = onlyDigits(value);
  if (digits.length !== 14 || /^([0-9])\1+$/.test(digits)) return false;
  const calculateDigit = (length: number) => {
    let sum = 0;
    let factor = length === 12 ? 5 : 6;
    for (let index = 0; index < length; index += 1) {
      sum += Number(digits[index]) * factor;
      factor = factor === 2 ? 9 : factor - 1;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };
  return calculateDigit(12) === Number(digits[12]) && calculateDigit(13) === Number(digits[13]);
};

const GuestCompressorAccess: React.FC = () => {
  const { productType } = useParams<Params>();
  const validProduct = productType === 'hermetico' || productType === 'semi-hermetico';
  const [authorized, setAuthorized] = useState(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!saved) return false;
    try {
      const session = JSON.parse(saved);
      return session.productType === productType && session.expiresAt > Date.now();
    } catch (_) {
      sessionStorage.removeItem(SESSION_KEY);
      return false;
    }
  });
  const [form, setForm] = useState({ fullName: '', corporateEmail: '', whatsapp: '', cnpj: '', profile: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!validProduct) return <div>Produto não encontrado.</div>;
  if (authorized) return <Compressores />;

  const update = (field: string, value: string) => setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (form.cnpj && !isValidCnpj(form.cnpj)) {
      setError('Informe um CNPJ válido.');
      return;
    }
    if (onlyDigits(form.whatsapp).length < 10) {
      setError('Informe um WhatsApp válido.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/v1/public/compressor-access/${productType}`, {
        ...form,
        cnpj: form.cnpj || null,
      });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        token: response.data.accessToken,
        productType,
        expiresAt: Date.now() + response.data.expiresIn * 1000,
      }));
      setAuthorized(true);
    } catch (_) {
      setError('Não foi possível liberar o acesso. Verifique os dados informados.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GuestPage>
      <Eyebrow>Acesso externo</Eyebrow>
      <Title>Consulte seu compressor</Title>
      <Description>
        Para liberar o conteúdo do compressor {productType === 'hermetico' ? 'Hermético' : 'Semi-Hermético'}, preencha seus dados.
      </Description>
      <Form onSubmit={submit}>
        <FullWidthField>
          Nome completo*
          <Input required placeholder="Digite seu nome completo" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} />
        </FullWidthField>
        <Field>
          E-mail corporativo*
          <Input required type="email" placeholder="nome@empresa.com.br" value={form.corporateEmail} onChange={(e) => update('corporateEmail', e.target.value)} />
        </Field>
        <Field>
          WhatsApp*
          <Input required inputMode="tel" placeholder="(00) 00000-0000" value={form.whatsapp} onChange={(e) => update('whatsapp', maskWhatsapp(e.target.value))} />
        </Field>
        <Field>
          CNPJ
          <Input inputMode="numeric" placeholder="00.000.000/0000-00" value={form.cnpj} onChange={(e) => update('cnpj', maskCnpj(e.target.value))} />
        </Field>
        <Field>
          Perfil profissional*
          <Select required value={form.profile} onChange={(e) => update('profile', e.target.value)}>            
          <option value="" disabled>Selecione o perfil</option>
          <option value="ELGIN">ELGIN</option><option value="LOJISTA">LOJISTA</option>
          <option value="PROJETISTA">PROJETISTA</option><option value="INTEGRADOR">INTEGRADOR</option>
          <option value="CLIENTE_FINAL">CLIENTE FINAL</option><option value="OUTROS">OUTROS</option>
          </Select>
        </Field>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <SubmitButton type="submit" disabled={loading}>{loading ? 'Liberando acesso...' : 'Acessar produto'}</SubmitButton>
      </Form>
    </GuestPage>
  );
};

export default GuestCompressorAccess;
